import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Play, 
  Layers, 
  ExternalLink, 
  Bookmark, 
  Send, 
  Check, 
  Briefcase, 
  Search, 
  SlidersHorizontal, 
  Plus, 
  Trash2, 
  Eye, 
  Database, 
  AlertTriangle, 
  Sparkles, 
  Info, 
  DollarSign, 
  Video, 
  Tv, 
  Monitor, 
  Cpu, 
  Download, 
  RefreshCw, 
  FileText,
  User,
  ShoppingBag,
  HelpCircle,
  X,
  Compass,
  ArrowRight,
  TrendingUp,
  Tag,
  ShieldAlert,
  ChevronRight,
  Filter,
  BarChart,
  Grid
} from 'lucide-react';
import { MarketplaceCategoryTabs } from './MarketplaceCategoryTabs';
import { SmartGadgetCard } from './SmartGadgetCard';

export interface GadgetProduct {
  id: string;
  title: string;
  category: string;
  shortDescription: string;
  productImage: string;
  sourceVideoId?: string;
  sourceVideoUrl?: string;
  sourceChannelName?: string;
  sourceChannelUrl?: string;
  retailerName: string;
  productUrl: string;
  affiliateUrl: string;
  linkType: 'external-retailer' | 'affiliate-link' | 'creator-product-link' | 'manual-research' | 'supplier-lead' | 'future-product' | 'nexus-owned-stock-future';
  priceText: string;
  availabilityText: string;
  disclosureLabel: string;
  tags: string[];
  priority: number;
  productStatus: 'Draft' | 'Researching' | 'Published' | 'Needs Review' | 'Affiliate Ready' | 'Creator Link' | 'External Retailer' | 'Coming Soon' | 'Future Product' | 'Disabled';
  futureStatus: 'Available Now' | 'Coming Soon' | 'Future Tech' | 'Concept Product' | 'Pre-Order' | 'Supplier Research' | 'Trade Show Target' | 'Watchlist';
  wholesalePotential: 'High' | 'Medium' | 'Low' | 'Unknown';
  supplierStatus: 'Not Started' | 'Supplier Search Needed' | 'Supplier Found' | 'Quote Needed' | 'Sample Needed' | 'Compliance Review Needed' | 'Ready for Future Purchase';
  createdAt?: string;
  updatedAt?: string;
  clicks?: number;
  isSaved?: boolean;
}

export interface SupplierItem {
  id: string;
  productIdea: string;
  category: string;
  currentRetailExamples: string;
  potentialSupplier: string;
  supplierCountry: string;
  tradeShowSource: string;
  moq?: string;
  estimatedUnitCost: number;
  estimatedRetailPrice: number;
  marginEstimate?: number;
  complianceNotes: string;
  importNotes: string;
  status: 'Idea' | 'Researching' | 'Supplier Search Needed' | 'Supplier Found' | 'Quote Requested' | 'Sample Needed' | 'Compliance Review' | 'Ready for Future Purchase' | 'Not Suitable';
}

export interface ScoutWatchlist {
  id: string;
  title: string;
  category: string;
  comingSoonStatus: string;
  shortDescription: string;
  sourceVideo: string;
}

interface NexusSmartGadgetMarketplaceProps {
  isAdmin?: boolean;
  onAddLog?: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

const CATEGORIES_LIST = [
  'ALL',
  'AI Gadgets',
  'Smart Home',
  'Smart Home / Amazon Finds',
  'Cool Gadgets',
  'Amazon Gadgets',
  'Workspace Tech',
  'Creator Gear',
  'Repair Tools'
];

export const NexusSmartGadgetMarketplace: React.FC<NexusSmartGadgetMarketplaceProps> = ({
  isAdmin = false,
  onAddLog
}) => {
  const [gadgets, setGadgets] = useState<GadgetProduct[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierItem[]>([]);
  const [scoutWatchlist, setScoutWatchlist] = useState<ScoutWatchlist[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Search & Filter state
  const [activeTab, setActiveTab] = useState<'discover' | 'compare' | 'agent' | 'sourcing' | 'manager'>('discover');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState<string>('nodePriority');
  const [watchlistOnly, setWatchlistOnly] = useState(false);
  
  // Modals & popups
  const [selectedGadget, setSelectedGadget] = useState<GadgetProduct | null>(null);
  const [compareList, setCompareList] = useState<GadgetProduct[]>([]);
  
  // Custom Product Extraction State
  const [isExtractionOpen, setIsExtractionOpen] = useState(false);
  const [extractionVideoUrl, setExtractionVideoUrl] = useState('');
  const [extractionNotes, setExtractionNotes] = useState('');
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [draftedProduct, setDraftedProduct] = useState<Partial<GadgetProduct> | null>(null);

  // Manual Edit Gadget State
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [formGadget, setFormGadget] = useState<Partial<GadgetProduct>>({});

  // Supplier Pipeline Form state
  const [isSupplierFormOpen, setIsSupplierFormOpen] = useState(false);
  const [formSupplier, setFormSupplier] = useState<Partial<SupplierItem>>({});

  // Agent State
  const [scoutQuery, setScoutQuery] = useState('Workspace automation tools');
  const [agentRunning, setAgentRunning] = useState(false);
  const [agentOutput, setAgentOutput] = useState<string | null>(null);
  const [scoutedDrafts, setScoutedDrafts] = useState<GadgetProduct[]>([]);

  // Fetch initial cache
  const fetchMarketplaceData = async () => {
    setIsLoading(true);
    try {
      const endpoint = isAdmin ? '/api/admin/marketplace/future-gadgets' : '/api/marketplace/future-gadgets';
      const res = await fetch(endpoint);
      const data = await res.json();
      if (data?.success) {
        setGadgets(data.gadgets || []);
      }
    } catch (e) {
      console.error("Failed loading gadgets store:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch('/api/admin/marketplace/supplier-pipeline');
      const data = await res.json();
      if (data?.success) {
        setSuppliers(data.suppliers || []);
      }
    } catch (e) {
      console.error("Failed loading supplier pipeline:", e);
    }
  };

  const fetchScoutWatchlist = async () => {
    try {
      const res = await fetch('/api/admin/marketplace/product-scout');
      const data = await res.json();
      if (data?.success) {
        setScoutWatchlist(data.watchlist || []);
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchMarketplaceData();
    fetchScoutWatchlist();
    if (isAdmin) {
      fetchSuppliers();
    }
  }, [isAdmin]);

  // Handle save toggle
  const handleToggleWatchlist = async (g: GadgetProduct) => {
    try {
      const res = await fetch(`/api/marketplace/future-gadgets/${g.id}/save`, { method: 'POST' });
      const data = await res.json();
      if (data?.success) {
        setGadgets(prev => prev.map(item => item.id === g.id ? { ...item, isSaved: data.isSaved } : item));
        if (selectedGadget?.id === g.id) {
          setSelectedGadget(curr => curr ? { ...curr, isSaved: data.isSaved } : null);
        }
        if (onAddLog) {
          onAddLog(
            'Future Gadgets', 
            `${data.isSaved ? 'Saved item' : 'Removed item'}: ${g.title}`, 
            'SUCCESS', 
            `Watchlist state successfully synced to Node database.`
          );
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Click Tracker proxy
  const handleRecordClick = async (g: GadgetProduct) => {
    try {
      await fetch(`/api/marketplace/future-gadgets/${g.id}/click`, { method: 'POST' });
    } catch (e) {}
  };

  // Send to Product Scout Agent handler
  const handleDispatchScoutAgent = async (task: string, gadget: GadgetProduct) => {
    setAgentRunning(true);
    setAgentOutput(null);
    setActiveTab('agent');
    try {
      if (onAddLog) {
        onAddLog(
          'Future Gadgets', 
          `Scout intelligence request: "${task}"`, 
          'PENDING_APPROVAL', 
          `Dispatched neural prompt matrix for smart product: "${gadget.title}".`
        );
      }
      const res = await fetch(`/api/marketplace/future-gadgets/${gadget.id}/send-to-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task })
      });
      const data = await res.json();
      if (data?.success) {
        setAgentOutput(data.result);
        if (onAddLog) {
          onAddLog(
            'Future Gadgets', 
            `Agent response successfully rendered`, 
            'SUCCESS', 
            `Analysis report generated for "${gadget.title}".`
          );
        }
      } else {
        setAgentOutput("Agent synthesis timeout. Check system logs.");
      }
    } catch (err: any) {
      setAgentOutput(`Connection error: ${err?.message || err}`);
    } finally {
      setAgentRunning(false);
    }
  };

  // Run general AI product search
  const handleRunAgentSearch = async () => {
    setAgentRunning(true);
    setAgentOutput(null);
    try {
      const res = await fetch('/api/admin/marketplace/product-scout/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: scoutQuery })
      });
      const data = await res.json();
      if (data?.success && data.draftProduct) {
        setScoutedDrafts(prev => [data.draftProduct, ...prev]);
        setAgentOutput(`**TASK STATUS: RESOLVED ENTIRELY**\n
[NEXORAX FUTURE PRODUCT SCOUT - ANALYSIS COMPLETED]
Target Query Matrix: "${scoutQuery}"

1. SCOUTED PRODUCT GENERATED:
We have synthesized a verified upcoming smart gadget: **"${data.draftProduct.title}"** for Category: ${data.draftProduct.category}.
Estimated price target: ${data.draftProduct.priceText || 'N/A'}. 

We have loaded this product as a catalog draft. You can approve and publish it directly to the dashboard from the "Product Scout Agent Picks" draft rail below!`);
      }
    } catch (e: any) {
      setAgentOutput(`Agent failed: ${e?.message}`);
    } finally {
      setAgentRunning(false);
    }
  };

  // Approve a scouted draft with AI Assist
  const handleApproveDraft = async (draft: GadgetProduct) => {
    try {
      const res = await fetch('/api/admin/marketplace/future-gadgets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, productStatus: 'Published' })
      });
      const data = await res.json();
      if (data?.success) {
        alert(`Successfully approved and published "${draft.title}" to the marketplace!`);
        setScoutedDrafts(prev => prev.filter(d => d.id !== draft.id));
        fetchMarketplaceData();
      }
    } catch (e) {
      alert("Failed to approve draft.");
    }
  };

  // Compare List controls
  const handleToggleCompare = (g: GadgetProduct) => {
    if (compareList.some(item => item.id === g.id)) {
      setCompareList(prev => prev.filter(item => item.id !== g.id));
    } else {
      if (compareList.length >= 3) {
        alert("You can compare up to 3 products at a time.");
        return;
      }
      setCompareList(prev => [...prev, g]);
    }
  };

  // AI Assist extraction draft generator
  const handleGenerateExtractionDraft = async () => {
    if (!extractionVideoUrl) {
      alert("Please specify a YouTube Video URL first.");
      return;
    }
    setAiDraftLoading(true);
    try {
      const videoIdMatch = extractionVideoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube-nocookie\.com\/embed\/)([^#\&\?]*)/);
      const extractedId = videoIdMatch ? videoIdMatch[1] : 'akUKWP3QsX4';

      const res = await fetch('/api/admin/marketplace/product-scout/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: extractionNotes || "Smart Gadget from YouTube Video review" })
      });
      
      const data = await res.json();
      if (data?.success && data.draftProduct) {
        setDraftedProduct({
          ...data.draftProduct,
          sourceVideoId: extractedId,
          sourceVideoUrl: extractionVideoUrl,
          sourceChannelName: "@Aiunpack",
          sourceChannelUrl: "https://www.youtube.com/@Aiunpack",
          disclosureLabel: "Creator product link"
        });
        if (onAddLog) {
          onAddLog('Admin Console', `AI Extract Draft Generated`, 'SUCCESS', `Created draft details for video node: ${extractedId}`);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAiDraftLoading(false);
    }
  };

  // Save/Approve extraction form
  const handlePublishExtracted = async () => {
    if (!draftedProduct?.title) {
      alert("Please generate or edit the product details first.");
      return;
    }
    try {
      const res = await fetch('/api/admin/marketplace/future-gadgets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...draftedProduct,
          productStatus: draftedProduct.productStatus || 'Published'
        })
      });
      const data = await res.json();
      if (data?.success) {
        alert("Extracted Product successfully published!");
        setDraftedProduct(null);
        setExtractionVideoUrl('');
        setExtractionNotes('');
        setIsExtractionOpen(false);
        fetchMarketplaceData();
      }
    } catch (e) {
      alert("Failed publishing item.");
    }
  };

  // Edit form submit Handler
  const handleSaveGadgetForm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const endpoint = formGadget.id 
        ? `/api/admin/marketplace/future-gadgets/${formGadget.id}`
        : '/api/admin/marketplace/future-gadgets';

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formGadget)
      });
      const data = await res.json();
      if (data?.success) {
        alert(formGadget.id ? "Product updated successfully!" : "Product created successfully!");
        setFormGadget({});
        setIsEditorOpen(false);
        fetchMarketplaceData();
      }
    } catch (err) {
      alert("Save failed.");
    }
  };

  // Supplier form submit Handler
  const handleSaveSupplierForm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/admin/marketplace/supplier-pipeline', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formSupplier)
      });
      const data = await res.json();
      if (data?.success) {
        alert("Supplier data synced successfully.");
        setFormSupplier({});
        setIsSupplierFormOpen(false);
        fetchSuppliers();
      }
    } catch (e) {
      alert("Save failed.");
    }
  };

  // Delete product
  const handleDeleteProduct = async (id: string, name: string) => {
    if (!confirm(`Are you certain you want to delete "${name}"?`)) return;
    try {
      const res = await fetch(`/api/admin/marketplace/future-gadgets/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data?.success) {
        fetchMarketplaceData();
        if (onAddLog) {
          onAddLog('Admin Console', `Deleted gadget concept`, 'SUCCESS', `Asset [${id}] purged.`);
        }
      }
    } catch (e) {
      alert("Failed deleting asset.");
    }
  };

  // Help generate clean embed nocookie URL
  const getEmbedLink = (g: GadgetProduct) => {
    if (!g.sourceVideoId) {
      if (g.sourceVideoUrl) {
        const match = g.sourceVideoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube-nocookie\.com\/embed\/)([^#\&\?]*)/);
        if (match && match[1]) return `https://www.youtube-nocookie.com/embed/${match[1]}`;
      }
      return null;
    }
    return `https://www.youtube-nocookie.com/embed/${g.sourceVideoId}`;
  };

  // Filtering Logic
  const filteredGadgets = gadgets.filter(g => {
    const matchesSearch = g.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          g.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          g.tags.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    
    let matchesCategory = selectedCategory === 'ALL' || g.category === selectedCategory;
    
    // Status filters map
    let matchesStatus = true;
    if (selectedStatusFilter === 'Available Now') matchesStatus = g.futureStatus === 'Available Now';
    else if (selectedStatusFilter === 'Coming Soon') matchesStatus = g.futureStatus === 'Coming Soon';
    else if (selectedStatusFilter === 'Future Tech') matchesStatus = g.futureStatus === 'Future Tech';
    else if (selectedStatusFilter === 'AI Gadgets') matchesStatus = g.category === 'AI Gadgets';
    else if (selectedStatusFilter === 'Smart Home') matchesStatus = g.category === 'Smart Home' || g.category === 'Smart Home / Amazon Finds';
    else if (selectedStatusFilter === 'Amazon Finds') matchesStatus = g.category.includes('Amazon');
    else if (selectedStatusFilter === 'Creator Gear') matchesStatus = g.category === 'Creator Gear';
    else if (selectedStatusFilter === 'Workspace Tech') matchesStatus = g.category === 'Workspace Tech' || g.category === 'PC & Workspace';
    else if (selectedStatusFilter === 'Wholesale Candidate') matchesStatus = g.wholesalePotential === 'High';
    else if (selectedStatusFilter === 'Affiliate Ready') matchesStatus = (g.productStatus === 'Affiliate Ready' || g.linkType === 'affiliate-link');
    else if (selectedStatusFilter === 'External Retailer') matchesStatus = g.linkType === 'external-retailer';

    const matchesWatchlist = !watchlistOnly || g.isSaved;

    return matchesSearch && matchesCategory && matchesStatus && matchesWatchlist;
  });

  // Sorting Logic
  const sortedGadgets = [...filteredGadgets].sort((a, b) => {
    if (sortBy === 'name') {
      return a.title.localeCompare(b.title);
    } else if (sortBy === 'nodePriority') { // Node priority
      return (b.priority || 0) - (a.priority || 0);
    } else if (sortBy === 'latest') {
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    } else if (sortBy === 'futurePotential') {
      const map: Record<string, number> = { 'Future Tech': 3, 'Coming Soon': 2, 'Available Now': 1 };
      return (map[b.futureStatus] || 0) - (map[a.futureStatus] || 0);
    } else if (sortBy === 'mostClicked') {
      return (b.clicks || 0) - (a.clicks || 0);
    } else if (sortBy === 'wholesalePotential') {
      const map: Record<string, number> = { 'High': 3, 'Medium': 2, 'Low': 1, 'Unknown': 0 };
      return (map[b.wholesalePotential] || 0) - (map[a.wholesalePotential] || 0);
    }
    return 0;
  });

  return (
    <div className="w-full text-slate-100 min-h-screen font-sans bg-black p-1 md:p-4 select-none relative overflow-hidden">
      {/* GLOW DECORATIONS */}
      <div className="absolute top-[-100px] left-[-100px] w-96 h-96 bg-[#e840f3]/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 right-[-100px] w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-[150px] pointer-events-none animate-pulse" />

      {/* HEADER HERO AREA */}
      <div className="relative overflow-hidden rounded-2xl border border-purple-500/20 bg-gradient-to-br from-zinc-950 via-[#0b0416] to-black p-6 md:p-8 mb-6 shadow-[0_0_35px_rgba(232,64,243,0.07)]">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-3 max-w-3xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-950/40 border border-[#e840f3]/40 text-[9px] font-mono font-bold uppercase tracking-widest text-pink-400">
              <Sparkles className="w-3.5 h-3.5 text-[#e840f3] animate-pulse" />
              OUT OF THIS GALAXY DISCOVERY NODE
            </div>
            <h1 className="text-2xl md:text-4xl font-black tracking-tight text-white uppercase font-sans">
              Future Gadgets &amp; <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#e840f3] to-cyan-400">Smart Tech</span>
            </h1>
            <p className="text-slate-400 text-xs leading-relaxed font-mono">
              Watch the latest smart gadget videos, discover new tech products, compare buying options, and track future products before they go mainstream. Nexus Future Gadget Discovery helps users explore smart gadgets, AI devices, workspace technology, upcoming designs through reviews, external links, and evaluation pipelines.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 shrink-0 bg-zinc-950/80 border border-zinc-900 p-1.5 rounded-xl self-start font-mono text-[9px] font-black uppercase tracking-wider">
            <button
              onClick={() => setActiveTab('discover')}
              className={`px-3 py-2 rounded-lg transition-all ${
                activeTab === 'discover'
                  ? 'bg-gradient-to-r from-purple-900/40 to-[#e840f3]/20 font-bold text-white border border-[#e840f3]/40'
                  : 'text-slate-400 hover:text-white border border-transparent'
              }`}
            >
              <Grid className="w-3 h-3 inline mr-1" /> Discover
            </button>
            <button
              onClick={() => setActiveTab('compare')}
              className={`px-3 py-2 rounded-lg transition-all relative ${
                activeTab === 'compare'
                  ? 'bg-gradient-to-r from-cyan-950/50 to-cyan-800/20 font-bold text-cyan-300 border border-cyan-500/40'
                  : 'text-slate-400 hover:text-white border border-transparent'
              }`}
            >
              <Layers className="w-3 h-3 inline mr-1" /> Compare ({compareList.length})
              {compareList.length > 0 && (
                <span className="absolute -top-1 -right-1 px-1 py-0.5 text-[8px] bg-cyan-400 text-black rounded-full font-sans font-extrabold">{compareList.length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('agent')}
              className={`px-3 py-2 rounded-lg transition-all ${
                activeTab === 'agent'
                  ? 'bg-[#e840f3]/15 text-pink-400 border border-[#e840f3]/30'
                  : 'text-slate-400 hover:text-white border border-transparent'
              }`}
            >
              <Cpu className="w-3 h-3 inline mr-1" /> Scout Agent
            </button>
            
            {isAdmin && (
              <>
                <button
                  onClick={() => setActiveTab('manager')}
                  className={`px-3 py-2 rounded-lg transition-all ${
                    activeTab === 'manager'
                      ? 'bg-purple-950/50 text-[#e840f3] border border-purple-500/40 font-bold'
                      : 'text-slate-400 hover:text-white border border-transparent'
                  }`}
                >
                  <SlidersHorizontal className="w-3 h-3 inline mr-1" /> Gadget Manager
                </button>
                <button
                  onClick={() => setActiveTab('sourcing')}
                  className={`px-3 py-2 rounded-lg transition-all ${
                    activeTab === 'sourcing'
                      ? 'bg-amber-950/50 text-amber-300 border border-amber-500/40'
                      : 'text-slate-400 hover:text-white border border-transparent'
                  }`}
                >
                  <Briefcase className="w-3 h-3 inline mr-1" /> Sourcing Pipeline
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* FILTER & CONTROL BAR (DISCOVER MODE ONLY) */}
      {activeTab === 'discover' && (
        <div className="bg-zinc-950/80 border border-zinc-900 rounded-2xl p-4 mb-6 space-y-4">
          <div className="flex flex-col lg:flex-row gap-3 items-center justify-between">
            {/* Search Input */}
            <div className="relative w-full lg:max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search premium quantum gadgets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-black border border-zinc-850 rounded-xl focus:border-[#e840f3]/50 focus:outline-none font-mono text-xs text-white"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Filters Collapse / Fast Selectors */}
            <div className="flex flex-wrap gap-2 w-full lg:w-auto uppercase font-mono text-[9px] tracking-wider select-none">
              <button
                onClick={() => setWatchlistOnly(!watchlistOnly)}
                className={`py-2 px-3 rounded-lg border flex items-center gap-1.5 cursor-pointer shrink-0 transition-all ${
                  watchlistOnly
                    ? 'bg-amber-500/15 text-amber-400 border-amber-500/35'
                    : 'bg-transparent text-slate-500 border-zinc-850 hover:text-slate-305'
                }`}
              >
                <Bookmark className="w-3.5 h-3.5" />
                Watchlist ({gadgets.filter(g => g.isSaved).length})
              </button>

              <select
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value)}
                className="bg-black border border-zinc-850 py-2 px-3 rounded-lg cursor-pointer text-slate-300 text-left"
              >
                <option value="ALL">ALL STATUSES / TAGS</option>
                <option value="Available Now">AVAILABLE NOW</option>
                <option value="Coming Soon">COMING SOON</option>
                <option value="Future Tech">FUTURE TECH</option>
                <option value="AI Gadgets">AI GADGETS TYPE</option>
                <option value="Smart Home">SMART HOME LABS</option>
                <option value="Amazon Finds">AMAZON FINDS</option>
                <option value="Creator Gear">CREATOR GEAR</option>
                <option value="Workspace Tech">WORKSPACE &amp; WORKSUITE</option>
                <option value="Wholesale Candidate">WHOLESALE HIGH-PROFIT CANDIDATES</option>
                <option value="Affiliate Ready">AFFILIATE INTEGRATED</option>
                <option value="External Retailer">EXTERNAL RETAILERS</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-black border border-zinc-850 py-2 px-3 rounded-lg cursor-pointer text-slate-305"
              >
                <option value="nodePriority">NODE PRIORITY</option>
                <option value="name">ALPHABETICAL NAME</option>
                <option value="latest">LATEST ADDED</option>
                <option value="futurePotential">FUTURE POTENTIAL</option>
                <option value="mostClicked">MOST POPULAR</option>
                <option value="wholesalePotential">WHOLESALE SCORE</option>
              </select>

              {isAdmin && (
                <div className="flex gap-1">
                  <button
                    onClick={() => setIsExtractionOpen(true)}
                    className="py-2 px-3 rounded-lg bg-cyan-950/50 text-cyan-400 border border-cyan-800/40 hover:bg-cyan-900/30 font-bold flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" /> Extract Products
                  </button>
                  <button
                    onClick={() => {
                      setFormGadget({
                        title: '',
                        category: 'AI Gadgets',
                        shortDescription: '',
                        productImage: '',
                        retailerName: '',
                        productUrl: '',
                        affiliateUrl: '',
                        linkType: 'external-retailer',
                        priceText: '£0.00',
                        availabilityText: 'Available',
                        disclosureLabel: 'External retailer',
                        tags: [],
                        priority: 5,
                        productStatus: 'Draft',
                        futureStatus: 'Available Now',
                        wholesalePotential: 'Medium',
                        supplierStatus: 'Not Started'
                      });
                      setIsEditorOpen(true);
                    }}
                    className="py-2 px-3 rounded-lg bg-purple-950/50 text-pink-400 border border-purple-800/40 hover:bg-purple-900/30 font-bold flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" /> Deploy Custom
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Core categories list row */}
          <MarketplaceCategoryTabs
            activeCategory={selectedCategory}
            categories={CATEGORIES_LIST}
            onSelectCategory={setSelectedCategory}
          />
        </div>
      )}

      {/* RENDER ACTIVE TAB */}
      <AnimatePresence mode="wait">
        
        {/* DISCOVER TAB */}
        {activeTab === 'discover' && (
          <motion.div
            key="discover_grid"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-8"
          >
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-24 gap-3">
                <RefreshCw className="w-8 h-8 text-[#e840f3] animate-spin" />
                <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Hydrating smart quantum tech cache...</p>
              </div>
            ) : sortedGadgets.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 bg-zinc-950/30 border border-zinc-900 rounded-2xl text-center">
                <Compass className="w-10 h-10 text-zinc-700 mb-3" />
                <p className="font-sans font-black text-white text-xs uppercase">No smart products loaded in grid</p>
                <p className="text-zinc-500 text-[11px] mt-1">Try resetting selected search term or filtering categories.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedGadgets.map((gadget) => (
                  <SmartGadgetCard
                    key={gadget.id}
                    gadget={gadget}
                    isAdmin={isAdmin}
                    isSaved={gadget.isSaved}
                    isCompared={compareList.some(item => item.id === gadget.id)}
                    onToggleWatchlist={handleToggleWatchlist}
                    onToggleCompare={handleToggleCompare}
                    onSelectGadget={(g) => {
                      handleRecordClick(g);
                      setSelectedGadget(g);
                    }}
                    onEditGadget={(g) => {
                      setFormGadget(g);
                      setIsEditorOpen(true);
                    }}
                    onDeleteGadget={handleDeleteProduct}
                    onTagClick={setSelectedCategory}
                  />
                ))}
              </div>
            )}

            {/* PUBLIC DISCLAIMER DISCLOSURES block */}
            <div className="bg-zinc-950/60 border border-zinc-900 p-5 rounded-2xl text-[9px] text-zinc-500 leading-relaxed max-w-4xl mx-auto space-y-2 select-none font-mono">
              <p>
                <strong className="text-zinc-400 uppercase">Nexus Public Disclaimer:</strong> Nexus Future Gadget Discovery may include external retailer links, creator product links, affiliate links, product research cards, and future technology watchlist items. Prices, availability, delivery, and product details may change. Always check the retailer or source before purchasing.
              </p>
              <p>
                <strong className="text-zinc-400 uppercase">Affiliate Disclosure:</strong> Some links may be affiliate links. NexoraX may earn a commission if you purchase through them, at no extra cost to you. As an Amazon Associate, NexoraX may earn from qualifying purchases if approved or verified.
              </p>
            </div>
          </motion.div>
        )}

        {/* DETAILS COMPARE TAB */}
        {activeTab === 'compare' && (
          <motion.div
            key="compare_sheet"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="bg-zinc-950/80 border border-zinc-900 p-6 rounded-2xl">
              <h3 className="font-mono text-xs uppercase font-extrabold text-cyan-400 flex items-center gap-1.5 mb-1">
                <Layers className="w-4 h-4" /> Hardware Feature Comparison Matrix
              </h3>
              <p className="text-[11px] text-slate-400 font-sans">Compare specifications, estimated wholesale margins, and source channels for up to 3 smart gadget candidates.</p>
              
              {compareList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Compass className="w-10 h-10 text-zinc-800 mb-3" />
                  <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-wider">No elements selected for side-by-side verification</p>
                  <p className="text-xs text-[#e840f3] mt-2 cursor-pointer font-bold uppercase underline" onClick={() => setActiveTab('discover')}>Return to main Discovery to choose</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                  {compareList.map((g) => (
                    <div key={g.id} className="bg-black border border-zinc-900 rounded-xl p-5 space-y-4 relative">
                      <button
                        onClick={() => handleToggleCompare(g)}
                        className="absolute top-4 right-4 text-slate-500 hover:text-white"
                      >
                        <X size={16} />
                      </button>

                      <div className="aspect-video bg-zinc-950 rounded-lg overflow-hidden border border-zinc-900 select-none">
                        <img src={g.productImage || "https://images.unsplash.com/photo-1546776310-eef45dd6d63c?w=400"} alt="preview" className="w-full h-full object-cover" />
                      </div>

                      <div className="space-y-1">
                        <span className="text-[8px] font-mono text-pink-400 uppercase tracking-widest">{g.category}</span>
                        <h4 className="font-sans font-black text-white text-sm uppercase">{g.title}</h4>
                      </div>

                      <div className="divide-y divide-zinc-900 font-mono text-[10px] space-y-1.5 pt-2">
                        <div className="flex justify-between py-1.5">
                          <span className="text-zinc-500 uppercase">STATUS LEVEL</span>
                          <span className="text-white font-bold">{g.futureStatus}</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span className="text-zinc-500 uppercase">EST RETAIL PRICE</span>
                          <span className="text-amber-400 font-bold">{g.priceText}</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span className="text-zinc-500 uppercase">RETAILER</span>
                          <span className="text-slate-300 font-bold">{g.retailerName || 'Sourced'}</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span className="text-zinc-500 uppercase">WHOLESALE FEASIBILITY</span>
                          <span className={`font-black ${g.wholesalePotential === 'High' ? 'text-emerald-400' : 'text-zinc-400'}`}>{g.wholesalePotential}</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span className="text-zinc-500 uppercase">SOURCE REVIEW CHANNEL</span>
                          <span className="text-cyan-400 font-bold normal-case">{g.sourceChannelName || '@Unknown'}</span>
                        </div>
                      </div>

                      <button
                        onClick={() => setSelectedGadget(g)}
                        className="w-full py-2.5 bg-[#e840f3]/15 hover:bg-[#e840f3]/25 border border-[#e840f3]/30 rounded-lg font-mono text-[9px] font-black uppercase text-white tracking-widest cursor-pointer"
                      >
                        Active Action Hub
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* PRODUCT SCOUT AI AGENT VIEW */}
        {activeTab === 'agent' && (
          <motion.div
            key="scout_view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="bg-zinc-950/85 border border-[#e840f3]/30 p-6 rounded-2xl shadow-[0_0_20px_rgba(232,64,243,0.03)]/5">
              <div className="flex items-center gap-2 mb-2">
                <Cpu className="w-5 h-5 text-[#e840f3] animate-pulse" />
                <h3 className="font-mono text-xs uppercase font-extrabold text-white">Nexus Future Product Scout AI Agent</h3>
              </div>
              <p className="text-[11px] text-slate-400 font-sans leading-relaxed mb-6">
                Direct the scout neural node to index upcoming smart gadgets, kickstarter innovations, find supplier leads or organize wholesale potential.
              </p>

              <div className="flex gap-2 max-w-2xl bg-black border border-zinc-900 rounded-xl p-1.5">
                <input
                  type="text"
                  placeholder="Ask Scout Agent to find a niche (e.g. Smart Projector, Bio-trackers)"
                  value={scoutQuery}
                  onChange={(e) => setScoutQuery(e.target.value)}
                  className="bg-black flex-1 focus:outline-none px-4 text-xs font-mono text-white"
                />
                <button
                  onClick={handleRunAgentSearch}
                  disabled={agentRunning}
                  className="py-2.5 px-5 bg-gradient-to-r from-purple-900/30 to-[#e840f3]/25 border border-[#e840f3]/35 rounded-lg font-mono text-[9px] font-black uppercase tracking-wider text-pink-400 cursor-pointer disabled:opacity-50"
                >
                  {agentRunning ? 'Scouting Grid...' : 'Run Scout'}
                </button>
              </div>

              {agentOutput && (
                <div className="mt-6 border border-cyan-500/20 bg-zinc-950 p-5 rounded-xl text-xs font-mono leading-relaxed text-slate-300 whitespace-pre-wrap">
                  {agentOutput}
                </div>
              )}
            </div>

            {/* AI DRIVEN DISCOVERED PICKS RAIL */}
            {scoutedDrafts.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-mono text-xs font-extrabold text-[#e840f3] uppercase tracking-wider">★ Product Scout Draft Proposals</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {scoutedDrafts.map((draft, dIdx) => (
                    <div key={dIdx} className="bg-zinc-950/10 border border-cyan-500/10 rounded-xl p-4 flex gap-4">
                      <img src={draft.productImage} className="w-16 h-16 rounded-lg object-cover border border-zinc-900 shrink-0" />
                      <div className="space-y-1.5">
                        <span className="text-[8px] font-mono bg-cyan-950/30 px-2 py-0.5 rounded text-cyan-400 border border-cyan-900/10 uppercase tracking-widest">{draft.category}</span>
                        <h4 className="font-sans font-black text-white text-xs uppercase">{draft.title}</h4>
                        <p className="text-[10px] text-slate-400 line-clamp-1">{draft.shortDescription}</p>
                        <div className="flex gap-2 pt-1 font-mono text-[8px] uppercase font-black tracking-widest">
                          <button
                            onClick={() => handleApproveDraft(draft)}
                            className="bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 px-2.5 py-1 rounded cursor-pointer"
                          >
                            Approve &amp; Publish
                          </button>
                          <button
                            onClick={() => setScoutedDrafts(prev => prev.filter((_, i) => i !== dIdx))}
                            className="text-slate-500 hover:text-rose-405 self-center"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* WATCHLIST PREVIEW */}
            <div className="bg-zinc-950/80 border border-zinc-900 p-6 rounded-2xl space-y-4">
              <h4 className="font-mono text-xs font-black uppercase text-pink-400 tracking-wider">Future Sourcing Guard Watchlist</h4>
              <p className="text-[10px] text-slate-400 font-sans leading-relaxed">
                These conceptual products are actively registered in the NexoraX Scout database, waiting for wholesale feasibility clearance.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {scoutWatchlist.map((sw) => (
                  <div key={sw.id} className="bg-black border border-zinc-900 rounded-xl p-4 flex justify-between items-center">
                    <div>
                      <span className="text-[8px] font-mono text-cyan-400 block uppercase font-bold tracking-wider">{sw.category} • {sw.comingSoonStatus}</span>
                      <h5 className="font-sans font-black text-white text-xs uppercase mt-0.5">{sw.title}</h5>
                      <p className="text-[10px] text-zinc-500 line-clamp-1 mt-0.5">{sw.shortDescription}</p>
                    </div>
                    <button 
                      onClick={() => {
                        setSelectedCategory(sw.category);
                        setActiveTab('discover');
                      }}
                      className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 text-[9px] font-mono uppercase rounded hover:border-cyan-400 hover:text-white shrink-0 text-slate-400"
                    >
                      Search Category
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* SMART GADGET MANAGER TAB (ADMIN_ONLY) */}
        {activeTab === 'manager' && isAdmin && (
          <motion.div
            key="gadget_manager_dashboard"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-zinc-950/80 border border-zinc-900 p-5 rounded-2xl">
              <div>
                <h3 className="font-mono text-xs uppercase font-extrabold text-[#e840f3] flex items-center gap-1.5">
                  <SlidersHorizontal className="w-4 h-4" /> Quantum Smart Gadget Manager
                </h3>
                <p className="text-[10px] text-slate-400 mt-1">Superuser system management grid for publishing discoverable product cards, creator channels, and affiliate links.</p>
              </div>
              <button
                onClick={() => {
                  setFormGadget({
                    title: '',
                    category: 'AI Gadgets',
                    shortDescription: '',
                    productImage: '',
                    retailerName: '',
                    productUrl: '',
                    affiliateUrl: '',
                    linkType: 'external-retailer',
                    priceText: '£19.99',
                    availabilityText: 'Available',
                    disclosureLabel: 'External retailer link',
                    tags: ['AI', 'SmartTech'],
                    priority: 5,
                    productStatus: 'Published',
                    futureStatus: 'Available Now',
                    wholesalePotential: 'Medium',
                    supplierStatus: 'Not Started'
                  });
                  setIsEditorOpen(true);
                }}
                className="py-2.5 px-4 bg-[#e840f3] hover:bg-[#e840f3]/90 text-black border border-purple-400/30 rounded-xl font-mono text-[9px] font-black uppercase tracking-wider flex items-center gap-1.5 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" /> Deploy New Smart Tech
              </button>
            </div>

            {/* GADGET RECORDS DATABASE LIST */}
            <div className="bg-zinc-950/80 border border-zinc-900 rounded-2xl overflow-hidden font-mono text-[10px]">
              <div className="overflow-x-auto">
                <table className="w-full text-left uppercase text-slate-400">
                  <thead className="bg-black/80 border-b border-zinc-900 text-slate-500 text-[8.5px] tracking-wider">
                    <tr>
                      <th className="p-4 font-bold">Smart Gadget Asset</th>
                      <th className="p-4 font-bold">Category</th>
                      <th className="p-4 font-bold">Retail Price</th>
                      <th className="p-4 font-bold">Link Type / Route</th>
                      <th className="p-4 font-bold">Wholesale Score</th>
                      <th className="p-4 font-bold">State</th>
                      <th className="p-4 font-bold text-right" style={{ minWidth: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-900">
                    {gadgets.map((g) => (
                      <tr key={g.id} className="hover:bg-[#e840f3]/5 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <img src={g.productImage} className="w-10 h-10 object-cover rounded-lg border border-zinc-900" />
                            <div>
                              <div className="font-sans font-bold text-white text-[11px] normal-case">{g.title}</div>
                              <div className="text-[7.5px] text-zinc-500 normal-case max-w-[220px] truncate">{g.productUrl}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-pink-400 font-extrabold">{g.category}</td>
                        <td className="p-4 text-amber-500 font-extrabold">{g.priceText}</td>
                        <td className="p-4">
                          <span className="px-2 py-0.5 rounded text-[8px] font-mono bg-purple-950/40 text-pink-400 border border-purple-900/40">
                            {g.linkType}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded text-[8px] font-black ${
                            g.wholesalePotential === 'High' ? 'bg-emerald-950/40 text-emerald-400' :
                            g.wholesalePotential === 'Medium' ? 'bg-amber-950/40 text-amber-400' : 'bg-zinc-900 text-zinc-500'
                          }`}>
                            {g.wholesalePotential} Potential
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-0.5 rounded text-[8px] font-black ${
                            g.productStatus === 'Published' ? 'bg-cyan-950/30 text-cyan-400 border border-cyan-500/20' : 'bg-zinc-900 text-zinc-500'
                          }`}>
                            {g.productStatus}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <div className="inline-flex gap-2 justify-end">
                            <button
                              onClick={() => {
                                setFormGadget(g);
                                setIsEditorOpen(true);
                              }}
                              className="p-1 px-3 bg-zinc-900 border border-zinc-800 rounded hover:border-[#e840f3] hover:text-white"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(g.id, g.title)}
                              className="p-1.5 bg-red-950/10 border border-transparent hover:border-red-500/30 text-red-400 rounded hover:bg-black"
                            >
                              <Trash2 size={11} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* WHOLESALE PIPELINE TAB (ADMIN-ONLY) */}
        {activeTab === 'sourcing' && isAdmin && (
          <motion.div
            key="sourcing_pipeline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex justify-between items-center bg-zinc-950/80 border border-zinc-900 p-4 rounded-xl">
              <div>
                <h3 className="font-mono text-xs uppercase font-extrabold text-amber-500">Wholesale &amp; Supplier Sourcing Pipeline</h3>
                <p className="text-[10px] text-slate-400 mt-1">Estimations and supply details are reviewed by the Director and kept private from general public visitors.</p>
              </div>
              <button
                onClick={() => {
                  setFormSupplier({
                    productIdea: '',
                    category: 'AI Gadgets',
                    currentRetailExamples: '',
                    potentialSupplier: '',
                    supplierCountry: '',
                    tradeShowSource: '',
                    moq: '100',
                    estimatedUnitCost: 10,
                    estimatedRetailPrice: 39,
                    marginEstimate: 74,
                    complianceNotes: '',
                    importNotes: '',
                    status: 'Idea'
                  });
                  setIsSupplierFormOpen(true);
                }}
                className="py-2 px-3 bg-amber-500 hover:bg-amber-600 text-black border border-amber-400/40 rounded-xl font-mono text-[9px] font-black uppercase tracking-wider flex items-center gap-1 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" /> Log Supplier Route
              </button>
            </div>

            {/* PIPELINE TABLE */}
            <div className="bg-zinc-950/80 border border-zinc-900 rounded-2xl overflow-hidden font-mono text-[10px]">
              <div className="overflow-x-auto">
                <table className="w-full text-left uppercase text-slate-400">
                  <thead className="bg-black/80 border-b border-zinc-900 text-slate-500 text-[8.5px] tracking-wider">
                    <tr>
                      <th className="p-4 font-bold">Product Idea</th>
                      <th className="p-4 font-bold">Category</th>
                      <th className="p-4 font-bold">Procurement Cost</th>
                      <th className="p-4 font-bold">Est Retail (Margin)</th>
                      <th className="p-4 font-bold">Supplier Source</th>
                      <th className="p-4 font-bold">Status</th>
                      <th className="p-4 font-bold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-900">
                    {suppliers.map(sup => (
                      <tr key={sup.id} className="hover:bg-zinc-900/30 transition-colors">
                        <td className="p-4">
                          <div className="font-sans font-bold text-white text-[11px] normal-case">{sup.productIdea}</div>
                          <div className="text-[8px] text-slate-500">MOQ target: {sup.moq || 'N/A'}</div>
                        </td>
                        <td className="p-4 text-cyan-400 font-extrabold">{sup.category}</td>
                        <td className="p-4 text-[#f59e0b] font-extrabold">£{(sup.estimatedUnitCost || 0).toFixed(2)}</td>
                        <td className="p-4">
                          <div>£{(sup.estimatedRetailPrice || 0).toFixed(2)}</div>
                          <div className="text-[8px] text-emerald-400 block font-black">
                            {sup.marginEstimate || Math.round(((sup.estimatedRetailPrice - sup.estimatedUnitCost)/sup.estimatedRetailPrice)*100)}% MARGIN
                          </div>
                        </td>
                        <td className="p-4 normal-case text-zinc-300">
                          <div>{sup.potentialSupplier || 'Sourcing...'}</div>
                          <div className="text-[7.5px] text-zinc-500 uppercase">{sup.supplierCountry || 'Unknown'} ∙ {sup.tradeShowSource || 'None'}</div>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-0.5 rounded text-[8px] font-black bg-purple-950 text-pink-400 border border-purple-900/40">
                            {sup.status}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => {
                              setFormSupplier(sup);
                              setIsSupplierFormOpen(true);
                            }}
                            className="p-1.5 px-3 bg-zinc-900 border border-zinc-800 rounded hover:border-amber-400 hover:text-white"
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- MODALS & DRAWER SHEET ASSISTANTS --- */}

      {/* PRODUCT ACTION POP-UP / BOTTOM SHEET */}
      <AnimatePresence>
        {selectedGadget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedGadget(null)}
            className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-3 md:p-6"
          >
            <motion.div
              initial={{ scale: 0.95, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 15 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-zinc-950 border border-purple-500/30 rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(232,64,243,0.1)] flex flex-col justify-between"
            >
              {/* Header */}
              <div className="relative aspect-video bg-black overflow-hidden border-b border-zinc-900/80">
                {getEmbedLink(selectedGadget) ? (
                  <iframe
                    src={getEmbedLink(selectedGadget)!}
                    className="w-full h-full object-cover"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    title={selectedGadget.title}
                  />
                ) : (
                  <img src={selectedGadget.productImage} className="w-full h-full object-cover" />
                )}
                <button
                  onClick={() => setSelectedGadget(null)}
                  className="absolute top-4 right-4 p-2 bg-black/85 border border-zinc-850 rounded-full text-slate-400 hover:text-white cursor-pointer z-10"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <span className="text-[8px] font-mono px-2 py-0.5 bg-purple-950 text-[#e840f3] border border-purple-900/40 rounded uppercase tracking-widest font-black leading-none">{selectedGadget.category}</span>
                  <h3 className="font-sans font-black text-lg text-white uppercase mt-1 leading-tight">{selectedGadget.title}</h3>
                </div>

                <p className="text-[11px] text-slate-400 leading-relaxed font-sans font-medium">{selectedGadget.shortDescription}</p>

                <div className="grid grid-cols-2 gap-4 bg-zinc-950 p-3 rounded-lg border border-zinc-900 font-mono text-[9.5px]">
                  <div>
                    <span className="text-zinc-500 uppercase block">Estimated retail price</span>
                    <span className="text-amber-400 font-bold text-sm tracking-tight">{selectedGadget.priceText || 'Check Retailer'}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 uppercase block">Source Review Channel</span>
                    <span className="text-cyan-400 font-bold text-sm tracking-tight">{selectedGadget.sourceChannelName || 'Verified Creator'}</span>
                  </div>
                </div>

                {/* Secure buying disclaimer details */}
                <div className="text-[8px] font-mono text-zinc-505 leading-relaxed bg-black/60 p-3 rounded border border-zinc-900/60 uppercase select-none">
                  <span className="text-[#e840f3] font-black">★ Disclosure: </span> Nexus Discovery lists curated third-party items with affiliate eligibility disclosure labels. This is not a stock-holding retailer yet. Prices, availability, and delivery remain governed by external storefronts.
                </div>
              </div>

              {/* Actions Footer */}
              <div className="p-6 bg-black/50 border-t border-zinc-900 flex flex-col sm:flex-row gap-3 text-[10px] font-mono uppercase font-black tracking-widest justify-between items-center">
                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => handleToggleWatchlist(selectedGadget)}
                    className={`py-3 px-4 w-full sm:w-auto rounded-xl border flex items-center justify-center gap-1.5 cursor-pointer transition-all ${
                      selectedGadget.isSaved
                        ? 'bg-amber-500/10 border-amber-500/40 text-amber-400'
                        : 'bg-zinc-900 border-zinc-800 text-slate-400 hover:text-white font-bold'
                    }`}
                  >
                    <Bookmark size={14} className={selectedGadget.isSaved ? "fill-amber-400 text-amber-400" : ""} />
                    {selectedGadget.isSaved ? "In Watchlist" : "Save to Watchlist"}
                  </button>
                  <button
                    onClick={() => handleToggleCompare(selectedGadget)}
                    className="p-3 bg-zinc-900 rounded-xl text-cyan-400 border border-zinc-850 flex items-center justify-center cursor-pointer transition-all"
                    title="Toggle product comparison matrix"
                  >
                    <Layers size={14} />
                  </button>
                </div>

                <div className="flex gap-2 w-full sm:w-auto justify-end">
                  <button
                    onClick={() => handleDispatchScoutAgent("Check wholesale margin potential", selectedGadget)}
                    className="py-3 px-4 bg-black border border-cyan-800/40 text-cyan-400 rounded-xl cursor-pointer hover:bg-cyan-950/20 font-bold"
                  >
                    Scout AI
                  </button>

                  <a
                    href={selectedGadget.productUrl || selectedGadget.affiliateUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="py-3 px-6 w-full sm:w-auto bg-gradient-to-r from-purple-900/50 to-purple-850/40 hover:to-[#e840f3]/25 border border-purple-500/35 rounded-xl text-[#e840f3] hover:text-white font-bold cursor-pointer text-center flex items-center justify-center gap-1.5"
                  >
                    View Deal
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* EXTRACT PRODUCTS FROM VIDEO (ADMIN WORKFLOW) */}
      <AnimatePresence>
        {isExtractionOpen && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-xl bg-zinc-950 border border-cyan-500/25 rounded-2xl p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-mono text-xs font-extrabold text-cyan-400 uppercase">Extract Products From Video Review</h3>
                <button onClick={() => setIsExtractionOpen(false)} className="text-slate-500 hover:text-white"><X size={16} /></button>
              </div>

              <div className="space-y-4 font-mono text-[10.5px]">
                <div className="space-y-1.5">
                  <label className="text-zinc-500 uppercase block">YouTube Video Source URL</label>
                  <input
                    type="url"
                    placeholder="https://youtu.be/akUKWP3QsX4"
                    value={extractionVideoUrl}
                    onChange={(e) => setExtractionVideoUrl(e.target.value)}
                    className="w-full bg-black border border-zinc-850 p-2.5 rounded-lg text-white"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-zinc-500 uppercase block">Admin Verification Notes (Optional)</label>
                  <textarea
                    placeholder="Describe specific gadget timestamp or feature details..."
                    value={extractionNotes}
                    onChange={(e) => setExtractionNotes(e.target.value)}
                    className="w-full bg-black border border-zinc-850 p-2.5 rounded-lg text-white h-20 resize-none"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleGenerateExtractionDraft}
                  disabled={aiDraftLoading}
                  className="w-full py-2.5 bg-cyan-900/20 text-cyan-400 border border-cyan-800/30 rounded-xl text-center uppercase tracking-widest font-black text-[9px] cursor-pointer"
                >
                  {aiDraftLoading ? "Scouting video metadata & drafting..." : "Generate AI Assist Draft card"}
                </button>

                {draftedProduct && (
                  <div className="border border-purple-500/20 bg-[#080210] p-4 rounded-xl space-y-3 pt-4">
                    <span className="text-[8px] font-mono text-[#e840f3] uppercase font-black block tracking-widest">★ AI generated draft (Ready for Approval)</span>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-[8px] block text-zinc-500">TITLE:</span>
                        <input
                          type="text"
                          value={draftedProduct.title || ''}
                          onChange={(e) => setDraftedProduct(prev => prev ? { ...prev, title: e.target.value } : null)}
                          className="w-full bg-black border border-zinc-900 p-1.5 rounded text-white"
                        />
                      </div>
                      <div>
                        <span className="text-[8px] block text-zinc-500">CATEGORY:</span>
                        <input
                          type="text"
                          value={draftedProduct.category || ''}
                          onChange={(e) => setDraftedProduct(prev => prev ? { ...prev, category: e.target.value } : null)}
                          className="w-full bg-black border border-zinc-900 p-1.5 rounded text-white"
                        />
                      </div>
                    </div>

                    <div>
                      <span className="text-[8px] block text-zinc-500">IMAGE EXAMPLES URL:</span>
                      <input
                        type="text"
                        value={draftedProduct.productImage || ''}
                        onChange={(e) => setDraftedProduct(prev => prev ? { ...prev, productImage: e.target.value } : null)}
                        className="w-full bg-black border border-zinc-900 p-1.5 rounded text-white"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-[8px] block text-zinc-500">RETAILER LINK:</span>
                        <input
                          type="text"
                          value={draftedProduct.productUrl || ''}
                          onChange={(e) => setDraftedProduct(prev => prev ? { ...prev, productUrl: e.target.value } : null)}
                          className="w-full bg-black border border-zinc-900 p-1.5 rounded text-white text-[8px]"
                        />
                      </div>
                      <div>
                        <span className="text-[8px] block text-zinc-500">DISCLOSURE LABEL:</span>
                        <select
                          value={draftedProduct.disclosureLabel || 'Creator product link'}
                          onChange={(e) => setDraftedProduct(prev => prev ? { ...prev, disclosureLabel: e.target.value } : null)}
                          className="w-full bg-black border border-zinc-900 p-1.5 rounded text-white"
                        >
                          <option value="Creator product link">Creator product link</option>
                          <option value="Affiliate link">Affiliate link</option>
                          <option value="External retailer">External retailer</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end pt-2">
                      <button
                        onClick={() => setDraftedProduct(null)}
                        className="py-1 px-3 text-slate-500 hover:text-white"
                      >
                        Reset
                      </button>
                      <button
                        onClick={handlePublishExtracted}
                        className="py-1.5 px-4 bg-[#e840f3] text-black font-extrabold rounded"
                      >
                        Approve &amp; Publish
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* EDIT GADGET MODAL */}
      <AnimatePresence>
        {isEditorOpen && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-xl bg-zinc-950 border border-purple-500/30 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-mono text-xs uppercase font-extrabold text-white">Deploy Custom Smart Gadget</h3>
                <button onClick={() => setIsEditorOpen(false)} className="text-slate-500 hover:text-white"><X size={16} /></button>
              </div>

              <form onSubmit={handleSaveGadgetForm} className="space-y-4 font-mono text-[10.5px]">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Product Title</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formGadget.title || ''}
                      onChange={(e) => setFormGadget(prev => ({ ...prev, title: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Category</label>
                    <select
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formGadget.category || 'AI Gadgets'}
                      onChange={(e) => setFormGadget(prev => ({ ...prev, category: e.target.value }))}
                    >
                      {CATEGORIES_LIST.filter(c => c !== 'ALL').map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-zinc-500 uppercase block">Short Description</label>
                  <textarea
                    className="w-full h-16 bg-black border border-zinc-850 p-2 rounded-lg text-white resize-none"
                    value={formGadget.shortDescription || ''}
                    onChange={(e) => setFormGadget(prev => ({ ...prev, shortDescription: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-zinc-500 uppercase block">Image URL</label>
                  <input
                    type="text"
                    className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white text-[8px]"
                    value={formGadget.productImage || ''}
                    onChange={(e) => setFormGadget(prev => ({ ...prev, productImage: e.target.value }))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">YouTube Video ID</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formGadget.sourceVideoId || ''}
                      placeholder="e.g. akUKWP3QsX4"
                      onChange={(e) => setFormGadget(prev => ({ ...prev, sourceVideoId: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Channel Name</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formGadget.sourceChannelName || ''}
                      placeholder="e.g. Aiunpack"
                      onChange={(e) => setFormGadget(prev => ({ ...prev, sourceChannelName: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Suggested Retail price</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formGadget.priceText || '£0.00'}
                      onChange={(e) => setFormGadget(prev => ({ ...prev, priceText: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Future status</label>
                    <select
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formGadget.futureStatus || 'Available Now'}
                      onChange={(e) => setFormGadget(prev => ({ ...prev, futureStatus: e.target.value as any }))}
                    >
                      <option value="Available Now">Available Now</option>
                      <option value="Coming Soon">Coming Soon</option>
                      <option value="Future Tech">Future Tech</option>
                      <option value="Concept Product">Concept Product</option>
                      <option value="Pre-Order">Pre-Order</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Wholesale Potential</label>
                    <select
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formGadget.wholesalePotential || 'Medium'}
                      onChange={(e) => setFormGadget(prev => ({ ...prev, wholesalePotential: e.target.value as any }))}
                    >
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                      <option value="Unknown">Unknown</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Product Status</label>
                    <select
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formGadget.productStatus || 'Draft'}
                      onChange={(e) => setFormGadget(prev => ({ ...prev, productStatus: e.target.value as any }))}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Researching">Researching</option>
                      <option value="Published">Published</option>
                      <option value="Coming Soon">Coming Soon</option>
                      <option value="Disabled">Disabled</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Retailer URL</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white text-[8px]"
                      value={formGadget.productUrl || ''}
                      onChange={(e) => setFormGadget(prev => ({ ...prev, productUrl: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Link Type</label>
                    <select
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formGadget.linkType || 'external-retailer'}
                      onChange={(e) => setFormGadget(prev => ({ ...prev, linkType: e.target.value as any }))}
                    >
                      <option value="external-retailer">External Retailer</option>
                      <option value="affiliate-link">Affiliate Link</option>
                      <option value="creator-product-link">Creator Product Link</option>
                      <option value="supplier-lead">Supplier Lead</option>
                      <option value="manual-research">Manual Research</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-3">
                  <button type="button" onClick={() => setIsEditorOpen(false)} className="text-slate-500 hover:text-white">Cancel</button>
                  <button type="submit" className="py-2.5 px-6 bg-[#e840f3] text-black font-black uppercase tracking-wider rounded-xl">Save Parameters</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* SOURCING ENTRY MODAL */}
      <AnimatePresence>
        {isSupplierFormOpen && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-xl bg-zinc-950 border border-amber-500/20 rounded-2xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-mono text-xs uppercase font-extrabold text-amber-500">Synchrone Wholesale Supplier Track</h3>
                <button onClick={() => setIsSupplierFormOpen(false)} className="text-slate-500 hover:text-white"><X size={16} /></button>
              </div>

              <form onSubmit={handleSaveSupplierForm} className="space-y-4 font-mono text-[10.5px]">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Product Idea</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formSupplier.productIdea || ''}
                      onChange={(e) => setFormSupplier(prev => ({ ...prev, productIdea: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Category</label>
                    <select
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formSupplier.category || 'AI Gadgets'}
                      onChange={(e) => setFormSupplier(prev => ({ ...prev, category: e.target.value }))}
                    >
                      {CATEGORIES_LIST.filter(c => c !== 'ALL').map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Supplier Factory Name</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formSupplier.potentialSupplier || ''}
                      onChange={(e) => setFormSupplier(prev => ({ ...prev, potentialSupplier: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Country</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formSupplier.supplierCountry || ''}
                      onChange={(e) => setFormSupplier(prev => ({ ...prev, supplierCountry: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <span className="text-[8px] block text-zinc-500 uppercase">Est Unit cost</span>
                    <input
                      type="number"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formSupplier.estimatedUnitCost || 0}
                      onChange={(e) => setFormSupplier(prev => ({ ...prev, estimatedUnitCost: parseFloat(e.target.value) }))}
                    />
                  </div>
                  <div>
                    <span className="text-[8px] block text-zinc-500 uppercase">Est resale price</span>
                    <input
                      type="number"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formSupplier.estimatedRetailPrice || 0}
                      onChange={(e) => setFormSupplier(prev => ({ ...prev, estimatedRetailPrice: parseFloat(e.target.value) }))}
                    />
                  </div>
                  <div>
                    <span className="text-[8px] block text-zinc-500 uppercase">Margin Target %</span>
                    <input
                      type="number"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formSupplier.marginEstimate || 0}
                      onChange={(e) => setFormSupplier(prev => ({ ...prev, marginEstimate: parseInt(e.target.value) }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Compliance Certification Notes</label>
                    <input
                      type="text"
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formSupplier.complianceNotes || ''}
                      placeholder="e.g. CE, UKCA, ROHS"
                      onChange={(e) => setFormSupplier(prev => ({ ...prev, complianceNotes: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-zinc-500 uppercase block">Status</label>
                    <select
                      className="w-full bg-black border border-zinc-850 p-2 rounded-lg text-white"
                      value={formSupplier.status || 'Idea'}
                      onChange={(e) => setFormSupplier(prev => ({ ...prev, status: e.target.value as any }))}
                    >
                      <option value="Idea">Idea Only</option>
                      <option value="Researching">Researching</option>
                      <option value="Supplier Search Needed">Supplier Search Needed</option>
                      <option value="Supplier Found">Supplier Found</option>
                      <option value="Quote Requested">Quote Requested</option>
                      <option value="Sample Needed">Sample Needed</option>
                      <option value="Compliance Review">Compliance Review</option>
                      <option value="Ready for Future Purchase">Ready for Future Purchase</option>
                      <option value="Not Suitable">Not Suitable</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-3">
                  <button type="button" onClick={() => setIsSupplierFormOpen(false)} className="text-slate-500 hover:text-white">Cancel</button>
                  <button type="submit" className="py-2.5 px-6 bg-amber-500 text-black font-black uppercase tracking-wider rounded-xl">Save Pipeline Record</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};
